import React, { useState, useEffect, useRef } from "react"
import { useAuth } from "../contexts/AuthContext"
import { useSocket } from "../contexts/SocketContext"
import ChatSystem from "../Components/ChatSystem"
import ConversationList from "../Components/ConversationList"
import UserList from "../Components/UserList"
import { chatService } from "../services/apichat"
import { useReduxStore } from "../hooks/useReduxStore"
import notificationSound from "../assets/sample.mp3"
import { useNavigate } from "react-router-dom"
import { ArrowLeftIcon } from "@heroicons/react/24/outline"

const DashboardPage = () => {
  const {getState}=useReduxStore()
  const {userData}=getState("AUTH")
  const {adminData,adminToken}=getState("ADMIN_AUTH")
  const user=Boolean(adminToken)?adminData:userData
  const { socket } = useSocket()
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [adminUserId, setAdminUserId] = useState(null)
  const [activeTab, setActiveTab] = useState("conversations")
  const [conversationListKey, setConversationListKey] = useState(0) // For forcing re-render
  const [notificationEnabled, setNotificationEnabled] = useState(true)
  const navigate = useNavigate()
  
  // Function to play notification sound
  const playSound = () => {
    if (notificationEnabled) {
      const audio = new Audio(notificationSound);
      audio.play().catch(error => {
        console.error("Error playing notification sound:", error);
      });
    }
  }

  const handleLogout = () => {
    // logout()
  }

  const handleUserSelect = userId => {
    setSelectedUserId(userId)
  }

  const handleConversationUpdate = conversation => {
    console.log("🔄 Conversation updated in DashboardPage:", conversation)
    console.log("📋 Conversation participants:", conversation.participants)
    console.log("🔄 Current conversationListKey:", conversationListKey)

    // Only update if conversation actually changed
    if (conversation && conversation._id) {
      // Force ConversationList to refresh by changing its key
      setConversationListKey(prev => {
        const newKey = prev + 1
        console.log("🔄 Updating conversationListKey from", prev, "to", newKey)
        return newKey
      })
    }
  }

  useEffect(() => {
    // For users, get admin info and set admin user ID
    if (user?.role === "user") {
      fetchAdminInfo()
    }
  }, [user])

  // Global socket event listeners - always active regardless of open conversation
  useEffect(() => {
    if (socket) {
      console.log(
        "🔌 Setting up GLOBAL socket event listeners in DashboardPage..."
      )

      // Remove existing listeners first to prevent duplicates
      socket.off("receive_message")
      socket.off("message_sent")
      socket.off("conversation_updated")
      socket.off("user_typing")
      socket.off("user_stopped_typing")
      socket.off("message_seen")
      socket.off("message_error")
      socket.off("mark_seen_success")
      socket.off("messages_seen")
      socket.off("mark_seen_error")
      socket.off("typing_error")

      // Global event handlers
      const handleGlobalReceiveMessage = data => {
        console.log("📥 GLOBAL receive_message:", data)
        console.log("📥 Current user role:", user?.role)
        console.log("📥 Is admin:", user?.role === "admin")

        // Play notification sound for new message
        playSound();

        // Update conversation list for admin
        if (user?.role === "admin" && data.conversation) {
          console.log(
            "🔄 Updating admin conversation list from receive_message"
          )
          setConversationListKey(prev => prev + 1)
        }
      }

      const handleGlobalMessageSent = data => {
        console.log("📤 GLOBAL message_sent:", data)
        console.log("📤 Current user role:", user?.role)
        console.log("📤 Is admin:", user?.role === "admin")

        // Update conversation list for admin
        if (user?.role === "admin" && data.conversation) {
          console.log("🔄 Updating admin conversation list from message_sent")
          setConversationListKey(prev => prev + 1)
        }
      }

      const handleGlobalConversationUpdated = data => {
        console.log("🔄 GLOBAL conversation_updated:", data)
        console.log("🔄 Current user role:", user?.role)
        console.log("🔄 Is admin:", user?.role === "admin")

        // Update conversation list for admin
        if (user?.role === "admin") {
          console.log(
            "🔄 Updating admin conversation list from conversation_updated"
          )
          setConversationListKey(prev => prev + 1)
        }
      }

      const handleGlobalMessageSeen = data => {
        console.log("👁️ GLOBAL message_seen:", data)
        // Force conversation list to refresh to show updated seen status
        setConversationListKey(prev => prev + 1)
      }

      // Set up global listeners
      socket.on("receive_message", handleGlobalReceiveMessage)
      socket.on("message_sent", handleGlobalMessageSent)
      socket.on("conversation_updated", handleGlobalConversationUpdated)
      socket.on("message_seen", handleGlobalMessageSeen)

      console.log("✅ GLOBAL socket event listeners set up in DashboardPage")

      return () => {
        // Clean up global event listeners
        socket.off("receive_message", handleGlobalReceiveMessage)
        socket.off("message_sent", handleGlobalMessageSent)
        socket.off("conversation_updated", handleGlobalConversationUpdated)
        socket.off("message_seen", handleGlobalMessageSeen)
        console.log("🧹 GLOBAL socket event listeners cleaned up")
      }
    }
  }, [socket, user])

  const fetchAdminInfo = async () => {
    try {
      console.log("Fetching admin info...")
      const response = await chatService.getAdminInfo()
      console.log("Admin info response:", response)

      if (response.admin && response.admin._id) {
        setAdminUserId(response.admin._id)
        console.log("Set admin user ID:", response.admin._id)
      } else {
        // Fallback to 'admin' string if admin info not available
        setAdminUserId("admin")
        console.log("Fallback: Using admin string")
      }
    } catch (error) {
      console.error("Error fetching admin info:", error)
      // Fallback to 'admin' string
      setAdminUserId("admin")
      console.log("Error fallback: Using admin string")
    }
  }

  return (
    <div className="h-screen flex flex-col w-full bg-[#212C43]">
 

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {user?.role !== "user" ? (
          <>
         

            {/* Chat Area */}
            <div 
              className={`flex-1 flex flex-col transition-all duration-300 ${
                selectedUserId ? 'flex absolute inset-0 z-20 bg-[#212C43] lg:relative lg:z-0' : 'hidden lg:flex'
              }`}
            >
              {selectedUserId ? (
                <ChatSystem
                  selectedUserId={selectedUserId}
                  isAdmin={true}
                  onConversationUpdate={handleConversationUpdate}
                  onBack={() => setSelectedUserId(null)}
                />
              ) : (
                <div
                  className="flex-1 flex items-center justify-center bg-blueGray"
                >
                  <div className="text-center px-4">
                    <h3 className="text-white font-medium text-xl ">Select a user to start chatting</h3>
                    <p className="text-gray-500 font-normal text-md">
                      Choose a user from the sidebar to begin your conversation
                    </p>
                  </div>
                </div>
              )}
            </div>
               {/* Admin Sidebar - User List */}
               <div
              className={`flex flex-col bg-blueGray border-borderGray border-l w-full lg:w-[300px] transition-all duration-300 ${
                selectedUserId ? 'hidden lg:flex' : 'flex'
              }`}
            >
              <div
                style={{
                  padding: "20px",
                  borderBottom: "1px solid #4A5567",
                  // background: "white",
                  position: "relative",
          height:"10vh"

                }}
              className="bg-blueGray shrink-0 flex items-center gap-3" // Added flex
              >
                <button 
                  onClick={() => navigate('/Admin/Dashboard')} 
                  className="lg:hidden text-white hover:bg-white/10 p-1 rounded-full"
                >
                  {/* <ArrowLeftIcon className="h-5 w-5" /> */}
                     <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
                </button>
                <h3 className="font-semibold text-lg" style={{ margin: 0, color: "white" }}>Conversation List</h3>
       
              </div>

              {activeTab === "conversations" ? (
                <ConversationList
                  key={conversationListKey}
                  onUserSelect={handleUserSelect}
                  selectedUserId={selectedUserId}
                  adminId={adminData?.id}
                />
              ) : (
                <UserList
                  onUserSelect={handleUserSelect}
                  selectedUserId={selectedUserId}
                />
              )}
            </div>
          </>
        ) : (
          /* User View - Direct Chat with Admin */
          <div className="flex-1 flex flex-col w-full">
            {adminUserId ? (
              <ChatSystem
                selectedUserId={adminUserId}
                isAdmin={false}
                onConversationUpdate={handleConversationUpdate}
              />
            ) : (
              <div
                className="flex-1 flex items-center justify-center bg-blueGray"
              >
                <div className="text-center px-4">
                  <h3 className="text-white font-medium text-lg " >Connecting to Meteorologist...</h3>
                  <p className="text-gray-500 font-medium text-md" >You can send messages to admin anytime</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default DashboardPage




